import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Collapse,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import DeleteSweepIcon from "@material-ui/icons/DeleteSweep";
import { makeStyles } from "@material-ui/core/styles";
import { useSnackbar } from "react-simple-snackbar";
import HistoryIcon from "@material-ui/icons/History";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import * as api from "../../api";
import Spinner from "../Spinner/Spinner";
import NoData from "../svgIcons/NoData";

const DATE_PRESETS = {
  today: "Today",
  yesterday: "Yesterday",
  last7: "Last 7 days",
  thisMonth: "This month",
  lastMonth: "Last month",
  last6Months: "Last 6 months",
  custom: "Custom",
};

function getDateRangeFromPreset(preset) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let start, end;

  switch (preset) {
    case DATE_PRESETS.today:
      start = new Date(today);
      end = new Date(today);
      end.setHours(23, 59, 59, 999);
      break;
    case DATE_PRESETS.yesterday:
      start = new Date(today);
      start.setDate(start.getDate() - 1);
      end = new Date(start);
      end.setHours(23, 59, 59, 999);
      break;
    case DATE_PRESETS.last7:
      start = new Date(today);
      start.setDate(start.getDate() - 6);
      end = new Date(today);
      end.setHours(23, 59, 59, 999);
      break;
    case DATE_PRESETS.thisMonth:
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today);
      end.setHours(23, 59, 59, 999);
      break;
    case DATE_PRESETS.lastMonth:
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59, 999);
      break;
    case DATE_PRESETS.last6Months:
      start = new Date(today);
      start.setMonth(start.getMonth() - 5);
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      end = new Date(today);
      end.setHours(23, 59, 59, 999);
      break;
    default:
      return null;
  }
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

const SIDEBAR_WIDTH = 80; // 5rem - matches navbar width

const useStyles = makeStyles((theme) => ({
  root: {
    marginLeft: SIDEBAR_WIDTH,
    padding: theme.spacing(3),
    marginTop: theme.spacing(2),
    minHeight: "100vh",
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  title: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    fontWeight: 600,
  },
  titleIcon: {
    fontSize: 28,
    color: theme.palette.primary.main,
  },
  tableCard: {
    borderRadius: 12,
    boxShadow: theme.shadows[2],
    overflow: "auto",
  },
  tableHeadCell: {
    padding: theme.spacing(2),
    fontWeight: 600,
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.grey[100],
  },
  tableCell: {
    padding: theme.spacing(2),
    fontSize: "0.9375rem",
  },
  filterBox: {
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  backButton: {
    textTransform: "none",
  },
  chipActive: {
    backgroundColor: theme.palette.success.light || "#c8e6c9",
    color: theme.palette.success.dark || "#2e7d32",
  },
  chipEnded: {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[700],
  },
  emptyState: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    padding: theme.spacing(8, 3),
    margin: theme.spacing(4, 0),
    backgroundColor: theme.palette.grey[50],
    borderRadius: 12,
    border: `1px dashed ${theme.palette.grey[400]}`,
  },
  employeeRow: {
    backgroundColor: theme.palette.grey[50],
    "&:hover": {
      backgroundColor: theme.palette.grey[100],
    },
  },
  dateRow: {
    backgroundColor: "#fafafa",
    "&:hover": {
      backgroundColor: theme.palette.grey[50],
    },
  },
  shiftRow: {
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  expandCell: {
    width: 48,
    padding: theme.spacing(1),
  },
  indentCell: {
    paddingLeft: theme.spacing(4),
  },
  indentCell2: {
    paddingLeft: theme.spacing(6),
  },
  clearButton: {
    textTransform: "none",
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    "&:hover": {
      borderColor: theme.palette.error.dark,
      backgroundColor: theme.palette.error.light + "20",
    },
  },
  dateField: {
    marginRight: theme.spacing(2),
    minWidth: 150,
  },
}));

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

function groupLogsByEmployeeAndDate(logs) {
  const byEmployee = {};
  logs.forEach((log) => {
    const empName = log.employeeName || "Unknown";
    if (!byEmployee[empName]) byEmployee[empName] = {};
    const dateKey = log.startTime
      ? new Date(log.startTime).toDateString()
      : "—";
    if (!byEmployee[empName][dateKey]) byEmployee[empName][dateKey] = [];
    byEmployee[empName][dateKey].push(log);
  });
  Object.keys(byEmployee).forEach((emp) => {
    Object.keys(byEmployee[emp]).forEach((dateKey) => {
      byEmployee[emp][dateKey].sort(
        (a, b) => new Date(b.startTime) - new Date(a.startTime)
      );
    });
  });
  return byEmployee;
}

export default function ShiftLogs() {
  const classes = useStyles();
  const history = useHistory();
  const [openSnackbar] = useSnackbar();
  const user = JSON.parse(localStorage.getItem("profile"));
  const defaultRange = useMemo(
    () => getDateRangeFromPreset(DATE_PRESETS.last7),
    []
  );
  const [logs, setLogs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filterEmployeeId, setFilterEmployeeId] = useState("");
  const [datePreset, setDatePreset] = useState(DATE_PRESETS.last7);
  const [startDate, setStartDate] = useState(defaultRange?.startDate || "");
  const [endDate, setEndDate] = useState(defaultRange?.endDate || "");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedEmployees, setExpandedEmployees] = useState({});
  const [expandedDates, setExpandedDates] = useState({});
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearFrom, setClearFrom] = useState("");
  const [clearTo, setClearTo] = useState("");
  const [clearing, setClearing] = useState(false);

  const effectiveDateRange = useMemo(() => {
    if (datePreset === DATE_PRESETS.custom) {
      return { startDate, endDate };
    }
    const range = getDateRangeFromPreset(datePreset);
    return range || { startDate, endDate };
  }, [datePreset, startDate, endDate]);

  const loadLogs = useCallback(async () => {
    const profile = JSON.parse(localStorage.getItem("profile"));
    if (!profile) {
      history.push("/login");
      return;
    }
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        startDate: effectiveDateRange.startDate,
        endDate: effectiveDateRange.endDate,
      };
      if (filterEmployeeId) params.employeeId = filterEmployeeId;
      const [logsRes, employeesRes] = await Promise.all([
        api.getShiftLogs(params),
        api.fetchEmployees(),
      ]);
      const data = logsRes.data || {};
      const logsList = Array.isArray(data.logs)
        ? data.logs
        : Array.isArray(data)
        ? data
        : [];
      setLogs(logsList);
      setTotalCount(data.totalCount ?? logsList.length);
      setEmployees(employeesRes.data || []);
    } catch (err) {
      setLogs([]);
      setTotalCount(0);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [history, page, rowsPerPage, effectiveDateRange.startDate, effectiveDateRange.endDate, filterEmployeeId]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleClearLogs = async () => {
    if (!clearFrom || !clearTo) {
      openSnackbar("Please select both start and end date");
      return;
    }
    if (new Date(clearFrom) > new Date(clearTo)) {
      openSnackbar("Start date must be before end date");
      return;
    }
    setClearing(true);
    try {
      const { data } = await api.clearShiftLogs(clearFrom, clearTo);
      openSnackbar(data?.message || `Cleared ${data?.recordsDeleted ?? 0} shift log(s). Your action has been recorded.`);
      setClearDialogOpen(false);
      setClearFrom("");
      setClearTo("");
      loadLogs();
    } catch (err) {
      openSnackbar(err?.response?.data?.message || "Failed to clear logs");
    } finally {
      setClearing(false);
    }
  };

  const grouped = useMemo(
    () => groupLogsByEmployeeAndDate(logs),
    [logs]
  );

  const toggleEmployee = (empName) => {
    setExpandedEmployees((prev) => ({
      ...prev,
      [empName]: !prev[empName],
    }));
  };

  const toggleDate = (empName, dateKey) => {
    const key = `${empName}|${dateKey}`;
    setExpandedDates((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!user) return null;

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            className={classes.backButton}
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push("/employees")}
          >
            Back
          </Button>
          <Typography variant="h5" className={classes.title}>
            <HistoryIcon className={classes.titleIcon} />
            Shift Logs
          </Typography>
        </Box>
      </Box>

      <Box className={classes.filterBox}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
          <InputLabel id="date-preset-label">Date range</InputLabel>
          <Select
            labelId="date-preset-label"
            value={datePreset}
            onChange={(e) => {
              const v = e.target.value;
              setDatePreset(v);
              setPage(0);
              if (v !== DATE_PRESETS.custom) {
                const range = getDateRangeFromPreset(v);
                if (range) {
                  setStartDate(range.startDate);
                  setEndDate(range.endDate);
                }
              }
            }}
            label="Date range"
          >
            <MenuItem value={DATE_PRESETS.today}>Today</MenuItem>
            <MenuItem value={DATE_PRESETS.yesterday}>Yesterday</MenuItem>
            <MenuItem value={DATE_PRESETS.last7}>Last 7 days</MenuItem>
            <MenuItem value={DATE_PRESETS.thisMonth}>This month</MenuItem>
            <MenuItem value={DATE_PRESETS.lastMonth}>Last month</MenuItem>
            <MenuItem value={DATE_PRESETS.last6Months}>Last 6 months</MenuItem>
            <MenuItem value={DATE_PRESETS.custom}>Custom</MenuItem>
          </Select>
        </FormControl>
        {datePreset === DATE_PRESETS.custom && (
          <>
            <TextField
              label="From date"
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              size="small"
              variant="outlined"
              className={classes.dateField}
            />
            <TextField
              label="To date"
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              size="small"
              variant="outlined"
              className={classes.dateField}
            />
          </>
        )}
        <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
          <InputLabel id="filter-employee-label">Search by employee</InputLabel>
          <Select
            labelId="filter-employee-label"
            value={filterEmployeeId}
            onChange={(e) => {
              setFilterEmployeeId(e.target.value);
              setPage(0);
            }}
            label="Search by employee"
          >
            <MenuItem value="">All employees</MenuItem>
            {employees.map((emp) => (
              <MenuItem key={emp._id} value={emp._id}>
                {emp.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DeleteSweepIcon />}
          className={classes.clearButton}
          onClick={() => {
            setClearFrom(effectiveDateRange.startDate);
            setClearTo(effectiveDateRange.endDate);
            setClearDialogOpen(true);
          }}
        >
          Clear logs
        </Button>
      </Box>

      <Dialog
        open={clearDialogOpen}
        onClose={() => !clearing && setClearDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { zIndex: 10000 } }}
      >
        <DialogTitle>Clear shift logs</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            Select date range to clear. Your action will be recorded (who, when, date range).
            Invoices are kept; only shift log entries are removed.
          </Typography>
          <TextField
            label="From date"
            type="date"
            value={clearFrom}
            onChange={(e) => setClearFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
            variant="outlined"
            style={{ marginBottom: 16 }}
          />
          <TextField
            label="To date"
            type="date"
            value={clearTo}
            onChange={(e) => setClearTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearDialogOpen(false)} disabled={clearing}>
            Cancel
          </Button>
          <Button
            onClick={handleClearLogs}
            color="secondary"
            variant="contained"
            disabled={clearing}
          >
            {clearing ? "Clearing…" : "Clear logs"}
          </Button>
        </DialogActions>
      </Dialog>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <Spinner />
        </Box>
      ) : logs.length === 0 ? (
        <Paper elevation={0} className={classes.emptyState}>
          <NoData />
          <Typography variant="subtitle1" style={{ marginTop: 16 }}>
            No shift logs yet
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Shift start and end times will appear here.
          </Typography>
        </Paper>
      ) : (
        <>
        <TableContainer component={Paper} className={classes.tableCard}>
          <Table aria-label="Shift logs">
            <TableBody>
              {Object.entries(grouped).map(([empName, datesMap]) => {
                const isEmpExpanded =
                  expandedEmployees[empName] === true;
                const dateKeys = Object.keys(datesMap).sort(
                  (a, b) => new Date(b) - new Date(a)
                );
                const totalShifts = dateKeys.reduce(
                  (sum, d) => sum + datesMap[d].length,
                  0
                );

                return (
                  <React.Fragment key={empName}>
                    <TableRow
                      className={classes.employeeRow}
                      onClick={() => toggleEmployee(empName)}
                      style={{ cursor: "pointer" }}
                    >
                      <TableCell className={classes.expandCell}>
                        <IconButton size="small">
                          {isEmpExpanded ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Typography
                          variant="body2"
                          style={{ fontWeight: 600, fontSize: "0.9375rem" }}
                        >
                          {empName}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                        >
                          {totalShifts} shift(s) across {dateKeys.length} day(s)
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.tableCell} colSpan={5} />
                    </TableRow>
                    <TableRow>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={7}
                      >
                        <Collapse
                          in={isEmpExpanded}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box margin={0}>
                            {dateKeys.map((dateKey) => {
                              const shiftList = datesMap[dateKey];
                              const dateExpandKey = `${empName}|${dateKey}`;
                              const isDateExpanded =
                                expandedDates[dateExpandKey] === true;

                              return (
                                <React.Fragment key={dateKey}>
                                  <Table size="small">
                                    <TableBody>
                                      <TableRow
                                        className={classes.dateRow}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleDate(empName, dateKey);
                                        }}
                                        style={{ cursor: "pointer" }}
                                      >
                                        <TableCell
                                          className={classes.expandCell}
                                          style={{ width: 48 }}
                                        >
                                          <IconButton size="small">
                                            {isDateExpanded ? (
                                              <ExpandLessIcon
                                                fontSize="small"
                                              />
                                            ) : (
                                              <ExpandMoreIcon
                                                fontSize="small"
                                              />
                                            )}
                                          </IconButton>
                                        </TableCell>
                                        <TableCell
                                          className={classes.tableCell}
                                          style={{ paddingLeft: 32 }}
                                        >
                                          <Typography
                                            variant="body2"
                                            style={{
                                              fontWeight: 500,
                                              fontSize: "0.875rem",
                                            }}
                                          >
                                            {dateKey}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            color="textSecondary"
                                          >
                                            {shiftList.length} shift(s)
                                          </Typography>
                                        </TableCell>
                                        <TableCell colSpan={5} />
                                      </TableRow>
                                      <TableRow>
                                        <TableCell
                                          colSpan={7}
                                          style={{
                                            paddingBottom: 0,
                                            paddingTop: 0,
                                          }}
                                        >
                                          <Collapse
                                            in={isDateExpanded}
                                            timeout="auto"
                                            unmountOnExit
                                          >
                                            <Table size="small">
                                              <TableHead>
                                                <TableRow>
                                                  <TableCell className={classes.tableHeadCell} style={{ width: 48 }} />
                                                  <TableCell className={classes.tableHeadCell} />
                                                  <TableCell className={classes.tableHeadCell}>Shift start</TableCell>
                                                  <TableCell className={classes.tableHeadCell}>Shift end</TableCell>
                                                  <TableCell className={classes.tableHeadCell} align="right">Invoices</TableCell>
                                                  <TableCell className={classes.tableHeadCell} align="right">Total billing (฿)</TableCell>
                                                  <TableCell className={classes.tableHeadCell}>Status</TableCell>
                                                </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                {shiftList.map((row) => (
                                                  <TableRow
                                                    key={row._id}
                                                    className={
                                                      classes.shiftRow
                                                    }
                                                  >
                                                    <TableCell
                                                      className={
                                                        classes.expandCell
                                                      }
                                                    />
                                                    <TableCell
                                                      className={
                                                        classes.tableCell
                                                      }
                                                      style={{
                                                        paddingLeft: 56,
                                                      }}
                                                    >
                                                      —
                                                    </TableCell>
                                                    <TableCell
                                                      className={
                                                        classes.tableCell
                                                      }
                                                    >
                                                      {formatDateTime(
                                                        row.startTime
                                                      )}
                                                    </TableCell>
                                                    <TableCell
                                                      className={
                                                        classes.tableCell
                                                      }
                                                    >
                                                      {formatDateTime(
                                                        row.endTime
                                                      )}
                                                    </TableCell>
                                                    <TableCell
                                                      className={
                                                        classes.tableCell
                                                      }
                                                      align="right"
                                                    >
                                                      {row.invoiceCount ?? 0}
                                                    </TableCell>
                                                    <TableCell
                                                      className={
                                                        classes.tableCell
                                                      }
                                                      align="right"
                                                    >
                                                      {typeof row
                                                        .totalBilling ===
                                                      "number"
                                                        ? row.totalBilling.toFixed(
                                                            2
                                                          )
                                                        : "0.00"}
                                                    </TableCell>
                                                    <TableCell
                                                      className={
                                                        classes.tableCell
                                                      }
                                                    >
                                                      <Chip
                                                        size="small"
                                                        label={
                                                          row.isActive
                                                            ? "Active"
                                                            : "Ended"
                                                        }
                                                        className={
                                                          row.isActive
                                                            ? classes.chipActive
                                                            : classes.chipEnded
                                                        }
                                                      />
                                                    </TableCell>
                                                  </TableRow>
                                                ))}
                                              </TableBody>
                                            </Table>
                                          </Collapse>
                                        </TableCell>
                                      </TableRow>
                                    </TableBody>
                                  </Table>
                                </React.Fragment>
                              );
                            })}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {totalCount > 0 && (
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage="Rows per page:"
          />
        )}
        </>
      )}
    </Box>
  );
}
